import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppSelector, useTranslate } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import { formatCents } from '@src/common';
import {
  adderResumeSelector,
  allTariffVerificationsSelector,
  checkTariffVerificationsIsValid,
  loadingAnyTariffVerificationsSelector,
  productPackageProductDiscountSelector,
  tariffsState,
  tariffVerificationValuesSelector,
  type FullProduct,
  type TariffsPerType,
} from '@src/stores/product';
import usePackageIdParam from '@src/hooks/usePackageIdParam';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import { track } from '@src/services/analytics';

import type { ScheduleTime } from '@src/core/buyerApiClient';
import type { TariffsPerTypeExtended } from '@src/domains/tariffs/core/tariffDisplay';
import { useTariffSelection } from '@src/domains/tariffs/hooks/useTariffSelection';
import { trackStepConfirmed } from '@src/domains/products/features/product-adder-v2/analytics/widgetTracking';

export type TariffSelectorStatus = 'idle' | 'empty' | 'ready';

export type UseTariffSelectorReturn = {
  placeholder: string;
  selectedSummary: string | null;
  disabled: boolean;
  open: boolean;
  onOpenChange: (next: boolean) => void;

  tariffs: TariffsPerTypeExtended[];
  product: FullProduct | undefined;
  status: TariffSelectorStatus;

  onUpdateQuantity: (tariffTypeId: string, quantity: number) => void;
  onConfirm: () => void;
  acceptableQuantitiesFor: (tariff: TariffsPerType) => [number, number];

  priceInfoFor: (tariff: TariffsPerType, useOldPrice?: boolean) => string;
  descriptionFor: (tariff: TariffsPerType) => string;
  isDependent: (tariffTypeId: string) => boolean;
  hasServiceFeeIn: (tariff: TariffsPerType) => boolean;
  calculateTariffIndex: (index: number) => number | null;
  packageDiscount: number | null;
  loadingVerifications: boolean;
  verificationsValid: boolean;
  totalLabel: string | null;

  showSingleError: (tariffTypeId: string) => boolean;
  showMultipleError: boolean;
  showIndependentError: (tariffTypeId: string) => boolean;
  showMaxAvailabilityError: (tariff: TariffsPerType) => boolean;
  hasAnyError: boolean;
  selectedHour: ScheduleTime | undefined;
};

export function useTariffSelector(): UseTariffSelectorReturn {
  const widgetId = useWidgetId();
  const { i18n } = useTranslate();

  const productId = useSelectedProductId();
  const packageId = usePackageIdParam();
  const { selectedDate } = useAppSelector(tariffsState(widgetId));
  const packageDiscountRaw = useAppSelector(productPackageProductDiscountSelector(widgetId, productId, packageId));
  const packageDiscount = packageDiscountRaw ? packageDiscountRaw : null;
  const loadingVerifications = useAppSelector(loadingAnyTariffVerificationsSelector);
  const allTariffVerifications = useAppSelector(allTariffVerificationsSelector);
  const tariffVerificationValues = useAppSelector(tariffVerificationValuesSelector(widgetId, productId));
  const { tariffsPriceCents } = useAppSelector(adderResumeSelector(widgetId, productId));

  // Shared tariff-selection orchestration (list + per-tariff presenters + quantity/error rules).
  // `disabled` (returned below) only tracks product load — epic dropped the date gate here.
  const {
    product,
    listOfTariffsPerType,
    tariffs,
    selectedHour,
    onUpdateQuantity,
    acceptableQuantitiesFor,
    priceInfoFor,
    descriptionFor,
    isDependent,
    calculateTariffIndex,
    showSingleError,
    showMultipleError,
    showIndependentError,
    showMaxAvailabilityError,
  } = useTariffSelection();

  // Derived from data, not from selling mode: the selector is "ready" once tariffs exist
  // (date-based fills via selectAvailableDate; without-date fills via the reducer mount). If
  // tariffs are empty and no date is picked, treat as "idle" (loading or awaiting date) so the
  // empty message doesn't flash. This keeps the hook out of sellingMode awareness.
  const dateSatisfied = Boolean(selectedDate) || listOfTariffsPerType.length > 0;

  const [open, setOpen] = useState(false);
  const onOpenChange = useCallback((next: boolean) => setOpen(next), []);

  const prevSelectedDateRef = useRef(selectedDate);
  useEffect(() => {
    if (prevSelectedDateRef.current !== selectedDate) {
      prevSelectedDateRef.current = selectedDate;
      setOpen(false);
    }
  }, [selectedDate]);

  const placeholder = i18n('product_booking_tariff_placeholder');

  const totalSelectedQuantity = useMemo(
    () => listOfTariffsPerType.reduce((acc, t: TariffsPerType) => acc + t.selectedQuantity, 0),
    [listOfTariffsPerType],
  );

  const selectedSummary = useMemo<string | null>(() => {
    if (totalSelectedQuantity <= 0) return null;
    return i18n('product_booking_tariff_summary', { count: totalSelectedQuantity });
  }, [totalSelectedQuantity, i18n]);

  const verificationsValid = useMemo(
    () => checkTariffVerificationsIsValid(listOfTariffsPerType, allTariffVerifications, tariffVerificationValues),
    [listOfTariffsPerType, allTariffVerifications, tariffVerificationValues],
  );

  const status: TariffSelectorStatus = useMemo(() => {
    if (!dateSatisfied || !product) return 'idle';
    if (listOfTariffsPerType.length === 0) return 'empty';
    return 'ready';
  }, [dateSatisfied, product, listOfTariffsPerType]);

  const onConfirm = useCallback(() => {
    if (!product) return;
    track('productAdderTariffSelected', {
      productId: product.id,
      productName: product.name,
      date: selectedDate ?? '',
      tariffs: totalSelectedQuantity,
    });
    trackStepConfirmed(widgetId, product.id, 'tariff');
    setOpen(false);
  }, [product, selectedDate, totalSelectedQuantity, widgetId]);

  const hasServiceFeeIn = useCallback(
    (tariff: TariffsPerType): boolean => tariff.schedulings.some((sch) => !!sch.tariffGroup?.serviceFee),
    [],
  );

  const hasAnyError = useMemo(() => {
    if (showMultipleError) return true;
    if (!product) return false;
    return tariffs.some((t) => showSingleError(t.type.id)) || tariffs.some((t) => showIndependentError(t.type.id));
  }, [showMultipleError, tariffs, product, showSingleError, showIndependentError]);

  const totalLabel = useMemo<string | null>(
    () => (tariffsPriceCents > 0 ? formatCents(tariffsPriceCents) : null),
    [tariffsPriceCents],
  );

  return {
    placeholder,
    selectedSummary,
    disabled: !product,
    open,
    onOpenChange,
    tariffs,
    product,
    status,

    onUpdateQuantity,
    onConfirm,
    acceptableQuantitiesFor,

    priceInfoFor,
    descriptionFor,
    isDependent,
    hasServiceFeeIn,
    calculateTariffIndex,
    packageDiscount,
    loadingVerifications,
    verificationsValid,
    totalLabel,

    showSingleError,
    showMultipleError,
    showIndependentError,
    showMaxAvailabilityError,
    hasAnyError,
    selectedHour,
  };
}
